import React, { useState, useCallback } from "react";
import { useToast } from "@chakra-ui/react";
import { useApolloClient } from "@apollo/client";
import type { CellObject } from "xlsx";
import { capitalize, keyBy } from "lodash";
import { DocumentType, gql } from "__generated__/gql";
import { SkuComponentType } from "__generated__/gql/graphql";
import { useCurrentBrandData } from "utils/currentUserHooks";
import { formatUpc } from "components/Shared/Forms/UpcInput";
import { DIMENSIONS_FIELDS } from "components/Shared/Forms/DimensionsFieldset";
import {
  CURRENCY_TWO_DECIMALS,
  INTEGER,
  TWO_DECIMALS,
  formatByColumnKey,
  makeFormatColumn,
  INCHES,
  CUBIC_FEET,
  POUNDS,
} from "utils/xlsxUtils";
import asyncVoidHandler from "utils/asyncVoidHandler";
import ExportMenuItem from "components/Shared/ExportMenuItem";
import { getWeightUnitExcelFormat } from "components/Shared/weightUnit";
import type { ProductFilterQueryVariables } from "./utils";
import {
  SkuComponentDimensionFields,
  skuComponentDimensionFields,
  skuComponentFieldPrefixByType,
  skuComponentUpcFields,
  SkuComponentUpcFields,
} from "./Import/utils";
import {
  SKU_COMPONENT_TYPE_LABELS,
  SKU_COMPONENT_TYPES,
} from "./SkuPanel/SkuComponents/utils";

const dataForPricesAndSpecsSheetQuery = gql(/* GraphQL */ `
  query DataForPricesAndSpecsSheet(
    $brandId: UUID!
    $productLineFilter: ProductLineFilter
    $skuFilter: SkuFilter
  ) {
    productLinesList(
      condition: { brandId: $brandId }
      filter: $productLineFilter
      orderBy: CREATED_AT_ASC
    ) {
      id
      name
      innerCaseQuantity
      wholesaleCaseQuantity
      wholesaleCasePrice
      skus: skusList(filter: $skuFilter, orderBy: CREATED_AT_ASC) {
        id
        name
        internalSkuId
        unitNetWeight
        unitNetWeightUnit
        grossPalletWeightLbs
        skuComponentsList {
          id
          skuComponentType
          upc
          grossWeightLbs
          dimensions {
            id
            height
            width
            depth
            cube
          }
        }
        tiHi {
          id
          ti
          hi
        }
        unitCogs
        unitSrp
      }
    }
  }
`);

type DataForPricesAndSpecsSheet = DocumentType<
  typeof dataForPricesAndSpecsSheetQuery
>;

type ProductLineDatum = NonNullable<
  DataForPricesAndSpecsSheet["productLinesList"]
>[number];
type SkuDatum = ProductLineDatum["skus"][number];
type RowDatum = ProductLineDatum | SkuDatum;

const columnKeys = [
  "rank",
  "itemOrderCode",
  "productLine",
  "skuName",
  ...skuComponentUpcFields,
  "unitNetWeight",
  "innerCaseQuantity",
  "masterCaseQty",
  ...skuComponentDimensionFields,
  "grossCaseWeight",
  "caseCube",
  "ti",
  "hi",
  "palletGrossWeight",
  "masterCasePrice",
  "suggestedRetailPrice",
] as const;

type ColumnKeys = typeof columnKeys[number];

const headersByKey: Record<ColumnKeys, string> = {
  rank: "Rank",
  itemOrderCode: "Item / Order Code",
  productLine: "Product Line",
  skuName: "Item Name",
  ...SKU_COMPONENT_TYPES.reduce((acc, sct) => {
    const prefix = skuComponentFieldPrefixByType[sct];
    acc[`${prefix}Upc`] = `${SKU_COMPONENT_TYPE_LABELS[sct]} UPC`;
    return acc;
  }, {} as Record<SkuComponentUpcFields, string>),
  unitNetWeight: "Unit size",
  innerCaseQuantity: "Inner Case Qty",
  masterCaseQty:
    "Master Case Qty (how many inner cases per master if applicable)",
  ...SKU_COMPONENT_TYPES.reduce((acc, sct) => {
    DIMENSIONS_FIELDS.forEach((f) => {
      const prefix = skuComponentFieldPrefixByType[sct];
      const dimField = capitalize(f) as Capitalize<typeof f>;
      acc[`${prefix}Dimension${dimField}`] = `${
        SKU_COMPONENT_TYPE_LABELS[sct]
      } ${f.slice(0, 1).toUpperCase()} (in)`;
    });
    return acc;
  }, {} as Record<SkuComponentDimensionFields, string>),
  grossCaseWeight: "Gross Case Weight (lb)",
  caseCube: "Case Cube",
  ti: "Ti",
  hi: "Hi",
  palletGrossWeight: "Pallet Gross Weight (lb)",
  masterCasePrice: "Master Case (FOB / Delivered) price",
  suggestedRetailPrice: "Suggested Retail Price (SRP)",
};

const formatsByKey: Partial<Record<ColumnKeys, string>> = {
  innerCaseQuantity: INTEGER,
  masterCaseQty: INTEGER,
  palletGrossWeight: POUNDS,
  masterCasePrice: CURRENCY_TWO_DECIMALS,
  suggestedRetailPrice: CURRENCY_TWO_DECIMALS,
  ...skuComponentDimensionFields.reduce((acc, f) => {
    acc[f] = INCHES;
    return acc;
  }, {} as Record<SkuComponentDimensionFields, string>),
  grossCaseWeight: POUNDS,
  caseCube: CUBIC_FEET,
  ti: INTEGER,
  hi: INTEGER,
};

const widthsByKey: Partial<Record<ColumnKeys, number>> = {
  rank: 6,
  itemOrderCode: 25,
  skuName: 25,
  productLine: 25,
  ...skuComponentUpcFields.reduce((acc, f) => {
    acc[f] = 16;
    return acc;
  }, {} as Record<SkuComponentUpcFields, number>),
  unitNetWeight: 13,
  ...skuComponentDimensionFields.reduce((acc, f) => {
    acc[f] = 12;
    return acc;
  }, {} as Record<SkuComponentDimensionFields, number>),
  caseCube: 13,
  ti: 6,
  hi: 6,
};

function safeFloat(value: Decimal | null | undefined) {
  if (!value) return null;
  return parseFloat(value);
}

function getWorksheetDataForPricesAndSpecsSheet(
  productLines: ProductLineDatum[],
) {
  const originalByRowIndex: Record<number, RowDatum> = {};

  const rowData: Partial<Record<ColumnKeys, string | number | null>>[] = [];
  let rank = 0;

  productLines.forEach((productLine) => {
    const baseRowDatum = {
      productLine: productLine.name,
      innerCaseQuantity: productLine.innerCaseQuantity,
      masterCaseQty: productLine.wholesaleCaseQuantity,
      masterCasePrice: safeFloat(productLine.wholesaleCasePrice),
    };

    if (!productLine.skus.length) {
      rank += 1;
      rowData.push({ ...baseRowDatum, rank });
      originalByRowIndex[rowData.length - 1] = productLine;
      return;
    }

    productLine.skus.forEach((sku) => {
      rank += 1;

      const skuComponentsByType = keyBy(
        sku.skuComponentsList,
        "skuComponentType",
      );

      const skuComponentFields = SKU_COMPONENT_TYPES.reduce((acc, sct) => {
        const skuComponent = skuComponentsByType[sct];
        if (!skuComponent) return acc;

        const prefix = skuComponentFieldPrefixByType[sct];

        if (skuComponent.upc) {
          acc[`${prefix}Upc`] = formatUpc(skuComponent.upc);
        }

        if (skuComponent.dimensions) {
          acc[`${prefix}DimensionHeight`] = skuComponent.dimensions.height;
          acc[`${prefix}DimensionWidth`] = skuComponent.dimensions.width;
          acc[`${prefix}DimensionDepth`] = skuComponent.dimensions.depth;
        }

        return acc;
      }, {} as Record<SkuComponentUpcFields | SkuComponentDimensionFields, string | null>);

      const caseSkuComponent = skuComponentsByType[SkuComponentType.CASE];

      rowData.push({
        ...baseRowDatum,
        ...skuComponentFields,
        rank,
        itemOrderCode: sku.internalSkuId,
        skuName: sku.name,
        unitNetWeight: safeFloat(sku.unitNetWeight),
        grossCaseWeight: safeFloat(caseSkuComponent?.grossWeightLbs),
        palletGrossWeight: safeFloat(sku.grossPalletWeightLbs),
        caseCube: safeFloat(caseSkuComponent?.dimensions?.cube),
        ti: sku.tiHi?.ti,
        hi: sku.tiHi?.hi,
        suggestedRetailPrice: safeFloat(sku.unitSrp),
      });
      originalByRowIndex[rowData.length - 1] = sku;
    });
  });

  const sheetData = [headersByKey, ...rowData].map((row) =>
    columnKeys.map((colKey) => row[colKey] ?? null),
  );

  return { sheetData, originalByRowIndex };
}

type Props = ProductFilterQueryVariables;

function ExportPricingAndSpecsButton({ productLineFilter, skuFilter }: Props) {
  const [isLoading, setIsLoading] = useState(false);

  const client = useApolloClient();
  const brandData = useCurrentBrandData();
  const toast = useToast();

  const handleClick = useCallback(async () => {
    setIsLoading(true);
    try {
      const { default: xlsx } = await import("xlsx");

      const { data } = await client.query({
        query: dataForPricesAndSpecsSheetQuery,
        variables: brandData?.id
          ? { brandId: brandData.id, productLineFilter, skuFilter }
          : undefined,
        fetchPolicy: "network-only",
      });

      if (!data) return;

      if (!data.productLinesList?.length) {
        toast({
          title:
            "Please add at least one product line to export a Pricing & Specs Sheet.",
          status: "warning",
          duration: 4000,
          isClosable: true,
        });
        return;
      }

      const { sheetData: worksheetData, originalByRowIndex } =
        getWorksheetDataForPricesAndSpecsSheet(data.productLinesList);
      const workbook = xlsx.utils.book_new();
      const worksheet = worksheetData && xlsx.utils.aoa_to_sheet(worksheetData);
      if (!worksheet) return;

      formatByColumnKey(
        await makeFormatColumn(worksheet),
        formatsByKey,
        columnKeys,
      );

      const unitNetWeightColIdx = columnKeys.indexOf("unitNetWeight");
      worksheetData.slice(1).forEach((row, idx) => {
        const ref = xlsx.utils.encode_cell({
          r: idx + 1,
          c: unitNetWeightColIdx,
        });
        const cellObj = worksheet[ref] as CellObject;
        if (!cellObj || cellObj.t !== "n") return;

        const datum = originalByRowIndex[idx];
        if (datum?.__typename !== "Sku") return;

        cellObj.z =
          (datum.unitNetWeightUnit &&
            getWeightUnitExcelFormat(datum.unitNetWeightUnit)) ||
          TWO_DECIMALS;
      });

      const columnWidths = columnKeys.map((colKey) => {
        const colWidth = widthsByKey[colKey];
        if (colWidth) return { width: colWidth };
        return { width: 15 };
      });

      worksheet["!cols"] = columnWidths;

      xlsx.utils.book_append_sheet(workbook, worksheet, "Pricing & Spec Sheet");

      xlsx.writeFile(
        workbook,
        `Pricing & Spec for ${brandData?.name || ""}.xlsx`,
      );
    } finally {
      setIsLoading(false);
    }
  }, [
    brandData?.name,
    brandData?.id,
    client,
    toast,
    productLineFilter,
    skuFilter,
  ]);

  return (
    <ExportMenuItem
      onClick={asyncVoidHandler(handleClick)}
      isLoading={isLoading}
    >
      Pricing & Spec Sheet
    </ExportMenuItem>
  );
}

export default ExportPricingAndSpecsButton;
